import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Button, PopoverContent, Separator } from '@xstore/shared/ui';
import type { LucideIcon } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Design tokens for every floating surface in the X Store topbar.    */
/* ------------------------------------------------------------------ */

export const surfaceTokens = {
  base: 'rounded-2xl border border-white/[0.06] bg-surface/95 shadow-[0_8px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl',
  header: 'flex items-center justify-between px-3.5 py-3 border-b border-white/[0.06]',
  title: 'text-sm font-semibold text-foreground',
  subtitle: 'text-xs text-muted',
  section: 'px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted',
  item: 'group flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm text-foreground transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40',
  itemActive: 'bg-[#2AABEE]/10 text-[#2AABEE]',
  itemInactive: 'hover:bg-white/[0.05]',
  itemDisabled: 'opacity-50 pointer-events-none',
  iconBox:
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.05] text-muted ring-1 ring-white/[0.06] transition-colors group-hover:bg-white/[0.08]',
  iconBoxActive: 'bg-[#2AABEE]/15 text-[#2AABEE] ring-[#2AABEE]/20',
  shortcut: 'ml-auto rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-muted',
  danger: 'text-danger hover:bg-danger/10',
} as const;

/* ------------------------------------------------------------------ */
/* PopoverPanel                                                         */
/* ------------------------------------------------------------------ */

interface PopoverPanelProps {
  children: ReactNode;
  className?: string;
  width?: string;
}

export function PopoverPanel({ children, className = '', width = 'w-72' }: PopoverPanelProps) {
  return (
    <PopoverContent
      className={`${width} overflow-hidden p-0 ${surfaceTokens.base} ${className}`}
    >
      {children}
    </PopoverContent>
  );
}

/* ------------------------------------------------------------------ */
/* SurfaceHeader                                                        */
/* ------------------------------------------------------------------ */

interface SurfaceHeaderProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

export function SurfaceHeader({ title, description, action }: SurfaceHeaderProps) {
  return (
    <div className={surfaceTokens.header}>
      <div className="min-w-0 flex-1">
        <h3 className={surfaceTokens.title}>{title}</h3>
        {description ? <p className={surfaceTokens.subtitle}>{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SurfaceSection                                                       */
/* ------------------------------------------------------------------ */

export function SurfaceSection({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <div className="py-2">
      {title ? <div className={surfaceTokens.section}>{title}</div> : null}
      <div className="space-y-0.5 px-1.5">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SurfaceDivider                                                       */
/* ------------------------------------------------------------------ */

export function SurfaceDivider() {
  return <Separator className="my-2 h-px bg-white/[0.06]" />;
}

/* ------------------------------------------------------------------ */
/* MenuItem                                                             */
/* ------------------------------------------------------------------ */

interface MenuItemProps {
  icon?: LucideIcon;
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ icon: Icon, label, shortcut, active, disabled, danger, onClick, children }, ref) => {
    const className = [
      surfaceTokens.item,
      active ? surfaceTokens.itemActive : surfaceTokens.itemInactive,
      disabled ? surfaceTokens.itemDisabled : '',
      danger && !active ? surfaceTokens.danger : '',
    ].join(' ');

    return (
      <button ref={ref} type="button" disabled={disabled} onClick={onClick} className={className}>
        {children ?? (Icon ? <MenuItemIcon Icon={Icon} active={active} /> : null)}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {shortcut ? <span className={surfaceTokens.shortcut}>{shortcut}</span> : null}
      </button>
    );
  },
);
MenuItem.displayName = 'MenuItem';

function MenuItemIcon({ Icon, active }: { Icon: LucideIcon; active?: boolean }) {
  return (
    <span className={`${surfaceTokens.iconBox} ${active ? surfaceTokens.iconBoxActive : ''}`}>
      <Icon size={16} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* IconButton                                                           */
/* ------------------------------------------------------------------ */

interface IconButtonProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, onClick, active, className = '' }, ref) => {
    return (
      <Button
        ref={ref}
        isIconOnly
        variant="ghost"
        aria-label={label}
        onPress={onClick}
        className={[
          'h-8 w-8 rounded-lg p-0 text-muted transition-all duration-150',
          'hover:bg-white/[0.06] hover:text-foreground',
          'focus-visible:ring-2 focus-visible:ring-[#2AABEE]/40',
          active ? 'bg-white/[0.06] text-foreground' : '',
          className,
        ].join(' ')}
      >
        {icon}
      </Button>
    );
  },
);
IconButton.displayName = 'IconButton';

/* ------------------------------------------------------------------ */
/* EmptyState                                                           */
/* ------------------------------------------------------------------ */

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
}

export function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
      {Icon ? (
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.04] text-muted">
          <Icon size={20} />
        </div>
      ) : null}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? <p className="mt-1 text-xs text-muted">{description}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* StatusDot                                                            */
/* ------------------------------------------------------------------ */

export type StatusColor = 'online' | 'offline' | 'warning' | 'inactive' | 'danger' | 'success';

interface StatusDotProps {
  color: StatusColor;
  pulse?: boolean;
  size?: 'sm' | 'md';
}

const statusColorClass: Record<StatusColor, string> = {
  online: 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]',
  offline: 'bg-red-500',
  warning: 'bg-amber-500',
  inactive: 'bg-slate-500',
  danger: 'bg-red-500',
  success: 'bg-emerald-500',
};

export function StatusDot({ color, pulse, size = 'sm' }: StatusDotProps) {
  const sizeClass = size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2';
  return (
    <span className="relative inline-flex">
      {pulse ? (
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${statusColorClass[color]}`}
        />
      ) : null}
      <span className={`relative inline-block rounded-full ${sizeClass} ${statusColorClass[color]}`} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* ShortcutHint                                                         */
/* ------------------------------------------------------------------ */

export function ShortcutHint({ keys }: { keys: string[] }) {
  return (
    <span className="hidden items-center gap-1 sm:flex">
      {keys.map((k) => (
        <kbd
          key={k}
          className="rounded-md bg-white/[0.08] px-1.5 py-0.5 text-[11px] font-medium text-muted"
        >
          {k}
        </kbd>
      ))}
    </span>
  );
}
